/**
 * # Recommendation matrix
 *
 * The recommendation matrix works by running through all possible combinations of single selections within an app path. Multi-select options are tested one selection at time, since for filtering purposes, typically only one value (the highest priority option) is used. Since matrix functionality is primarily useful for choice-based user paths, "specs" screens should be ignored (this is [configurable]{@link module:core/config.matrix}).
 *
 * Once the matrix is complete and results are gathered, they will be written to a CSV file that can be downloaded (unless in [`debug`]{@link module:core/config.matrix} mode). **Note that outputting this file is dependent on being run within the SRT Recommendation Matrix tool, either locally or on the [SRT dev tools]{@link https://srt.rweststaging.com/dev/tools/recommend-matrix/} site.** Running the matrix directly from `dist` without enabling `debug` in the build config will fail.
 *
 * To test or update a matrix build, for example on the [dev tools]{@link https://srt.rweststaging.com/dev/tools/recommend-matrix/} site:
 *
 * 1. Run a matrix build (make sure `settings.matrix.debug` is not enabled in build config) using the command `npm run build:matrix`. Any necessary build flags, e.g. `--lang`, `--platform`, or `--customization` **will** work in conjunction with this command.
 * 2. Take `dist` contents and move them into your testing or deployment location. If using the SRT Recommendation Matrix dev tool, this will be within a subdirectory of the tool.
 * 3. Once the build is in place, an option to select your version will be available within the tool, and it is ready to be provided an inventory to process.
 *
 * @module core/matrix
 */

import * as App from './app';
import * as Filter from './filter';
// import * as Analytics from './analytics';
import * as Util from './util';

/**
 * Default matrix configuration settings. These settings are exported to app default [`settings`]{@link module:core/app.settings}. For more information on configuration options, see [matrix content settings]{@link module:core/config.matrix}.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @example
 * // config defaults
 * {
 *   attributes: [],
 *   ignore: [],
 * }
 */
export const config = {
  attributes: [],
  ignore: [],
  blueprint: {
    show: {
      attributes: {
        app: true,
        filter: true,
      },
      conditionals: true,
      option_meta: true,
      page_ids: true,
      page_settings: true,
      filter_requests: true,
      product_reco_counts: true,
    },
  },
};

/**
 * Matrix internal settings object. Holds states and settings used during processing.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @example
 * // initial data values
 * {
 *   path: [],
 *   results: [],
 *   attributes: [],
 *   use_defined_attributes: false,
 *   blueprint: {
 *     nodes: {},
 *   },
 *   legend: [],
 *   product_count: 0,
 *   complete: false,
 * }
 */
let data = {
  path: [],
  results: [],
  attributes: [],
  use_defined_attributes: false,
  blueprint: {
    nodes: {},
  },
  product_reco_counts: {},
  product_count: 0,
  legend: [],
  complete: false,
};

/**
 * Initialize and run recommendation matrix.
 *
 * - Sets `env` setting to `matrix`.
 * - If [attributes]{@link module:core/config.matrix} are provided by app config, `data.use_defined_attributes` is enabled, and `data.attributes` are set to the provided attributes.
 * - App paths are [walked]{@link module:core/matrix~walk} until completed.
 * - Matrix recommendation results are [formatted]{@link module:core/matrix~formatResults} and stored under `matrix.results` in app settings.
 * - Finally, callback function is run.
 *
 * @since 3.0.0
 *
 * @method
 *
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see [`walk`]{@link module:core/matrix~walk}
 * @see [`formatResults`]{@link module:core/matrix~formatResults}
 * @see Used by:
 * @see [**`View.runMatrix`**]{@link module:core/view~runMatrix}
 */
export const init = () => {
  return new Promise((resolve) => {
    // update app env to matrix
    App.updateSettings({env: 'matrix'});
    // if attributes are provided by app config, set them to our data attributes
    // and tell the matrix to use them instead of generating
    if (App.settings.matrix.attributes.length) {
      data.attributes = App.settings.matrix.attributes;
      data.use_defined_attributes = true;
    }
    // walk until complete
    while (!data.complete) walk();
    // update app settings with formatted results
    App.updateSettings({
      matrix: {
        results: formatResults(),
        blueprint: formatBlueprint(),
      },
    });
    // resolve promise
    resolve();
  });
};

/**
 * Start walking matrix paths.
 *
 * Each "walk" represents a single journey through a sequence of choices. The end result of a walk is information about choices made during the walk, and a group of recommended products.
 *
 * At the beginning of each walk, [app data is reset]{@link module:core/app.start} for a fresh start. The first [`step`]{@link module:core/matrix~step} on the walk is then initiated with starting [view data]{@link module:core/matrix~parseView}, which recursively steps through choices until the end is reached and results can be stored in `[data.results]{@link module:core/matrix~data}`.
 *
 * If not using predefined product attributes (set in config), [attributes are gathered]{@link module:core/matrix~gatherProductAttributes} from the last walk results. Attributes are gathered after each walk so that attributes from all path variations will be represented. See [`matrix`]{@link module:core/config.matrix} configuration settings for information on using defined attributes.
 *
 * @method
 *
 * @since 3.0.0
 */
const walk = () => {
  // setup initial content, i.e. find the path to walk
  App.start(App.settings.start);
  // start recursive process with initial view, i.e. get to steppin'
  const result = step(parseView());
  // add data to results, i.e. write home about our travels
  data.results.push(result);
  // if we are using generated attributes, gather them here
  if (!data.use_defined_attributes) gatherProductAttributes();
  // add recommendation data to final node
  gatherRecoData();
};

/**
 * Step through each view in app path and get the current choice and related data. This function is called recursively until the end of a walk has reached and a recommendation result is returned.
 *
 * Each step is provided current [view data]{@link module:core/matrix~parseView}, which contains a list of choices that can be made. Steps also pass `result` and `path` objects through each recursive step, which are data about chosen choices, and a map of where the previous walk went, respectively. The `path` will ultimately be the basis for determining which choices should be selected in the next walk.
 *
 * The first walk has no previous path information, so the first choice (index `0`) from each view is selected. View title and choice label are collected for each choice and saved to the `results.choices` array. In addition, the index of the choices, as well as the total number of choices present in the current view is stored in the `path` array.
 *
 * After selecting a choice, the [next view data]{@link module:core/matrix~forward} is gathered, and the next step is taken.
 *
 * In the final step (once the `end` view has been reached, as defined in [app settings]{@link module:core/config.app_settings}), some setup for the next walk is necessary:
 * - The `path` is [iterated]{@link module:core/matrix~iterate} and saved so the next walk knows which choices to select.
 * - Product recommendations are added to the `results` object and returned.
 *
 * @since 3.0.0
 *
 * @param {object} view - Parsed view data. See [`parseView`]{@link module:core/matrix~parseView} for view object contents.
 * @param {object} result - Object containing choices and products gathered during walk. Passed recursively.
 * @param {array} result.choices - Array of choice objects selected during walk. Each choice object contains the properties `view` (the current view title) and `label` (the choice label).
 * @param {array} [result.products] - Added during final step, when product recommendations are available.
 * @param {array} path - Array of path node objects. Each node objects contains the properties `t` (total choice count in current view) and `i` (index of current choice). See [`iterate`]{@link module:core/matrix~iterate} for more information on `path`.
 *
 * @return {object}
 */
const step = (view, result = { choices: [] }, path = []) => {
  // as long as the end has not been reached, keep stepping
  if (App.state.current.location !== App.settings.end) {
    // otherwise keep stepping
    const index = path.length;
    let choice = {};

    // throw error if no choices are found
    if (!view.choices.length) throw `No choices found in view "${view.title}"`;

    // start choice node object
    let node = {
      t: view.choices.length,
      i: data.path[index] ? data.path[index].i : 0,
    };

    // get choice
    choice = view.choices[node.i];

    // add choice to results
    result.choices.push({
      group: choice.groupLabel,
      label: choice.label,
      t: node.t,
      i: node.i,
    });

    // push choice node
    path.push(node);

    // get next view content and take another step
    return step(forward(view, choice), result, path);
  }

  // once we've reached the end, iterate the last node
  iterate(path.length - 1, path);
  // set new path equal to our nodes
  data.path = path;
  // get product recommendations from the final view content
  result.products = view.products;
  // error if no products
  if (typeof result.products === 'undefined') throw('There was an issue finding product recommendations.');

  return result;
};

/**
 * Move forward in the path after a [`step`]{@link module:core/matrix~step}.
 *
 * This function updates app state based on choices made during each step, and provides the next view content formatted for use in the next step.
 *
 * - [App attributes are updated]{@link module:core/app.setAttributes} with `merge` enabled, so subfield choices don't overwrite parent choices.
 *
 * - If a choice has subfields, they are [parsed]{@link module:core/matrix~parseSubfields} to create the "new" view content. Content is returned, and the functions stops here.
 *
 * - Otherwise, the app state is moved [forward]{@link module:core/app.forward} and new view content is [parsed]{@link module:core/matrix~parseView} and returned.
 *
 * *Note that all choices will have a destination as long as either the choice or the view is originally provided one, since view destinations are added to choices during [`findChoices`]{@link module:core/matrix~findChoices}. If one is not found, an error will be thrown.*
 *
 * @since 3.0.0
 *
 * @param {object} view - View data used by the current step.
 * @param {object} choice - Selected choice object.
 *
 * @return {object}
 */
const forward = (view, choice) => {
  // update node state
  App.setAttributes([], true);
  App.setAttributes([choice], true);

  // if we have subfields, set up "view" with those choices
  if (choice.subfields) return parseSubfields(view, choice);

  // if no destination, throw error
  if (!choice.destination) throw `No destination found in view "${view.title}"`

  // get next view content
  App.forward(choice.destination);
  return parseView();
};

/**
 * Iterate path node choices to the next available choice. A path consists of nodes for each [`step`]{@link module:core/matrix~step} taken on a [`walk`]{@link module:core/matrix~walk}. During each step, an object is created that stores the total choice count in a view, `t`, as well as the index of the choice that was chosen, `i`.
 *
 * The purpose of the iterator is to prepare a path so that the next walk knows which choice to select. It works by taking the last choice made in a walk, and iterating the index of that choice to the next one available. If there are no more choices available in a node, the index is rolled around to `0` and the previous node's choices is iterated. This is done for each walk until the path nodes all possible iterations have been made, and the matrix is complete.
 *
 * During the first walk, no path exists yet, so the first choice is always selected (index `0`). The path created by the first walk (before iteration) will look something like this:
 *
 * ```
 * // walk #1
 * [
 *   { t: 2, i: 0 },
 *   { t: 2, i: 0 },
 * ]
 * ```
 *
 * In this example, there are two views with two choices each represented. To prepare the path for the next walk, the iterator starts with the last node (node `1`) and iterates `i`. The next walk will know which choices to select: the first in node `0`, and the second in node `1`. The second walk is provided a path that looks like this:
 *
 * ```
 * // walk #2
 * [
 *   { t: 2, i: 0 },
 *   { t: 2, i: 1 },
 * ]
 * ```
 *
 * Each time the iterator fully iterates a node, it calls itself again but iterates a previous node. So, after this walk is complete, since both choices in the last view have been walked, the iterator now rolls node `1`'s `i` back to `0`, moves to the previous node (node `0`), and iterates the choices there. The next walk will know to choose the second option in node `0`, and the first again in node `1`. The next two iterations look like this:
 *
 * ```
 * // walk #3
 * [
 *   { t: 2, i: 1 },
 *   { t: 2, i: 0 },
 * ]
 *
 * // walk #4
 * [
 *   { t: 2, i: 1 },
 *   { t: 2, i: 1 },
 * ]
 * ```
 *
 * By walk #4, all option combinations have been exhausted. The iterator knows walks are complete when it has fully iterated through node `0`, and sets `[data]{@link module:core/matrix~data}.complete: true`, which prevents additional [`walk`]{@link module:core/matrix~walk} attempts and begins data finalization.
 *
 * **Note that while each walk uses the previous path to inform its choice decision, it is also creating a *new* path each time, to account for possible route changes.** Changes in path route can happen if one choice leads to a set of different views than another, in which case the previous path's choices would not apply past the changed node. This is fine though, because all choices past any changed node are always index `0`. Only choices that are known to be the same are used to inform a given walk's choices.
 *
 * @since 3.0.0
 *
 * @param {number} step - The index of the path node to iterate.
 * @param {array} path - Array of path node objects. Path is created by a walk.
 */
const iterate = (step, path) => {
  // iterate the current step
  path[step].i++;

  // if path step has been iterated beyond available options, reset it and move to previous node
  if (path[step].i === path[step].t) {
    // reset current node choice index
    path[step].i = 0;
    // get index of previous node
    let prevIndex = step - 1;
    // keep iterating as long as we have a valid previous node index (0+)
    if (prevIndex >= 0) iterate(prevIndex, path);
    // if index is -1, we are done
    else data.complete = true;
  }
};

/**
 * Parse current view data into a format that is usable by the recommendation matrix.
 *
 * Matrix view content consists of a subset of normal current view properties (see [`App.setCurrentContent`]{@link module:core/app~setCurrentContent} for more information on current view properties). Properties included in matrix view:
 *
 * - `title` - View title
 * - `subtitle` - View subtitle, if one exists
 * - `destination` - Destination view ID, if one exists
 * - `products` - Array of recommended products, if they exist
 *
 * There is also a new property `choices`, which is an array of valid "choices" found within the view content. Choices are option objects [found]{@link module:core/matrix~findChoices} from `form` content types in the current view content.
 *
 * @since 3.0.0
 *
 * @return {object}
 *
 * @example
 * {
 *   title: "I want to use my computer for:",
 *   destination: "creativity",
 *   products: false,
 *   choices: [
 *     {
 *       label: "Browsing the internet",
 *       attributes: [ ... ]
 *     },
 *     {
 *       label: "Using a printer, scanner, or camera",
 *       attributes: [ ... ]
 *     },
 *     {
 *       label: "Multitasking",
 *       attributes: [ ... ]
 *     },
 *   ],
 * }
 */
const parseView = () => {
  let page_id = App.state.current.location;
  // init view object
  let view = {
    page_id,
    title: App.current.title || false,
    subtitle: App.current.subtitle || false,
    destination: App.current.destination || false,
    products: App.current.products || false,
  };

  // add blueprint data, if necessary
  if (!data.blueprint.nodes[page_id]) {
    // get node settings data, remove stuff that isn't useful
    let settings = Util.cloneDeep(App.state.current.settings);
    delete settings.destinations;
    delete settings.labels;
    delete settings.banner;
    // get side journey data
    let nodeLearnMore = App.current.learn_more ? [App.current.learn_more.destination] : [];
    let nodeDestinations = Object.keys(App.currentNodeSetting('destinations', {}));
    // add page blueprint data
    data.blueprint.nodes[page_id] = {
      title: view.title,
      destinations: [],
      sections: {},
      debug: [],
      groupConditions: {},
      side_journeys: [...nodeLearnMore, ...nodeDestinations],
      settings: Object.keys(settings).length ? settings : false,
      columns: (App.current.content && App.current.content[0] && App.current.content[0].columns && App.current.content[0].columns.length) || 0,
    }
    // if attributes exist, add condensed
    if (App.current.attributes) {
      let attributes = App.getAttributesFrom('active', null, true);
      data.blueprint.nodes[page_id].attr = attributes.attr;
      data.blueprint.nodes[page_id].attrMeta = attributes.attrMeta;
    }
  }

  // add subtitle and title to labels array, if they exist
  let labels = ['subtitle', 'title'].filter(label => view[label]).map(label => view[label]);
  // find choices in view content
  view.choices = findChoices(App.current, view.destination, labels);
  // if products exist in view, add unique names to legend
  if (view.products) Util.arrayPushUnique(data.legend, ...view.products.map(product => product.name));
  // return view content
  return view;
};

/**
 * Parse choice subfield content and create a "new" view to be processed as a matrix node. Subfield view content modifies the existing [parsed matrix view]{@link module:core/matrix~parseView}, sharing all previous content except for:
 *
 * - `title` - Same as previous view title, with addition of "+" to differentiate it
 * - `subtitle` - Previous view subtitle "+" (if one exists)
 * - `choices` - New choices are [found]{@link module:core/matrix~findChoices} from subfield content. Like in [`parseView`]{@link module:core/matrix~parseView}, the title and subtitle are sent as possible choice group labels.
 *
 * @since 3.0.0
 *
 * @param {object} view - Parsed matrix view content.
 * @param {object} choice - Current choice object.
 *
 * @return {object}
 */
const parseSubfields = (view, choice) => {
  let labels = [];
  // differentiate title and subtitle with + so subfield choices get their own columns
  for (let label of ['title', 'subtitle']) {
    if (view[label]) {
      view[label] = `${view[label]} +`;
      labels.unshift(view[label]);
    }
  }
  // find subfield choices
  view.choices = findChoices(choice.subfields, choice.destination, labels);
  // return view content
  return view;
};

/**
 * Find valid "choices" within content, and build an array of matrix-ready choice objects. Valid choices are any [form content type field]{@link module:core/config._content_type_form} [`options`]{@link module:core/config.options} present in a current view.
 *
 * This function is provided an object that contains view content, and checked recursively until fields with options are found. **Important: only top-level fields are returned during this step, as subfield content is parsed separately.**
 *
 * For each field with options, its label is added to the labels array as a possible label choice (if one exists). Then, each option in each field is copied and added to a single choices array. Additionally, some new properties are added to each object:
 *
 * - `groupLabel` - The first item in the `labels` array is added as the "group label". In order of priority, this may be: field label, view subtitle, or view title.
 * - `destination` - If the option does not already include a destination, it will be added from the provided view destination.
 *
 * @since 3.0.0
 *
 * @param {object|array} content - Object to search for choices within.
 * @param {string|boolean} destination - Destination view ID. If an option does not have a destination, this destination will be added to the choice object.
 * @param {array} labels - Array of possible group label values.
 * @param {array} [choices] - Array where found choices will be stored. Default: `[]`
 *
 * @return {array}
 *
 * @example
 * // form factor choices
 * [
 *   {
 *     label: "Laptops",
 *     groupLabel: "What type of devices are you looking for?",
 *     destination: "activities",
 *     attributes: [ ... ],
 *     subfields: [ ... ],
 *   },
 *   {
 *     label: "Desktops",
 *     groupLabel: "What type of devices are you looking for?",
 *     destination: "activities",
 *     attributes: [ ... ],
 *     subfields: [ ... ],
 *   },
 * ]
 */
const findChoices = (content, destination, labels, choices = []) => {
  let page_id = App.state.current.location;
  // shortcut to current blueprint object
  let node = data.blueprint.nodes[page_id];
  // copy labels
  labels = labels.slice();
  // if content is an object rather than an array, turn it into an array
  if (!Array.isArray(content)) content = Object.values(content);
  // check each array value
  for (let value of content) {
    // skip if value isn't an object
    if (typeof value !== 'object') continue;
    // if value includes a learn_more, add destination to side_journeys array
    if (value['learn_more'] && value['learn_more'].destination) {
      Util.arrayPushUnique(node.side_journeys, value['learn_more'].destination);
    }
    // if value has a label, add it to start of labels array
    if (value.label) labels.unshift(value.label);
    // store group conditionals
    if (value.conditional_on) {
      // get content path
      let contentPath = value.id ? Util.decodeID(value.id) : '';
      node.groupConditions[contentPath] = value.conditional_on;
    }
    // if value has options, gather choices
    if (value.options) {
      // build choices from options
      for (let i = 0; i < value.options.length; i++) {
        let o = value.options[i];
        // copy option
        let option = Util.cloneDeep(o);
        // add view destination to choice, if not present in option
        if (!option.destination) option.destination = destination;
        // if no destination, or destination should be ignored, continue
        if (!option.destination || App.settings.matrix.ignore.includes(option.destination)) continue;
        // add first label as the choice group label
        option.groupLabel = labels[0];
        // add condensed attribute data and meta
        let attributes = App.getAttributesFrom('inputs', [option], true);
        option.attr = attributes.attr;
        option.attrMeta = attributes.attrMeta;
        // create option group, if necessary
        // create unique field identifier
        let valueCopy = Util.cloneDeep(value);
        delete valueCopy.id;
        delete valueCopy.options;
        let field_id = Util.encodeID(JSON.stringify(valueCopy));
        if (!node.sections[field_id]) {
          // gather group conditions
          let path = Util.decodeID(value.id);
          let conditional_on = Object.entries(node.groupConditions).reduce((a, [p, c]) => path.includes(p) ? [...a, ...c] : a, []);
          // create field object
          node.sections[field_id] = {
            label: option.groupLabel,
            conditional_on,
            choices: {},
            columns: 0,
          };
          // tag settings to include
          let tags = ['field_type', 'required', 'allow_deselect'];
          for (let tag of tags) node.sections[field_id][tag] = value[tag] || false;
        }
        // add option to choices
        choices.push(option);
        // add choice label to legend
        if (option.label) Util.arrayPushUnique(data.legend, option.label);
        // add choice to blueprint data, if necessary
        // copy choice for modification
        let optionCopy = Util.cloneDeep(option);
        delete optionCopy.id;
        delete optionCopy.subfields;
        // create unique choice identifier
        let option_id = Util.encodeID(JSON.stringify(optionCopy));
        if (!node.sections[field_id].choices[option_id]) {
          node.sections[field_id].choices[option_id] = option;
          // add choice destination to node, if unique
          Util.arrayPushUnique(node.destinations, option.destination);
          // add choice destination to section if it does not exist
          if (typeof node.sections[field_id].destination === 'undefined') node.sections[field_id].destination = option.destination;
          // if a section destination does exist, but is different than the current option destination, remove destination altogether, as there is no single destination for this group
          else if (node.sections[field_id].destination !== option.destination) node.sections[field_id].destination = false;
          // if options are more than 4, and columns are present, add columns to section
          let choicesCount = Object.keys(node.sections[field_id].choices).length;
          let calcColumns = Util.round(choicesCount / 4);
          if (value.column_count && calcColumns > 1) {
            node.sections[field_id].columns = calcColumns;
          }
        }
      }
    }
    // otherwise look deeper
    else findChoices(value, destination, labels, choices);
  }
  return choices;
};

/**
 * Gathers all attribute properties (e.g. `processor`, `os`) associated with choices selected during a walk, and adds them to the [`data.attributes`]{@link module:core/matrix~data} array (if they don't already exist). These attributes are the product attributes that will be displayed alongside each matrix product result.
 *
 * @since 3.0.0
 */
const gatherProductAttributes = () => {
  // get all requested filter attributes
  let type = App.settings.primary_attribute.type;
  let walkAttributes = Object.keys(App.state.attr[type]);
  for (let attr of walkAttributes) {
    if (attr !== 'src') Util.arrayPushUnique(data.attributes, attr);
  }
};

const gatherRecoData = () => {
  // store currently recommended product IDs
  let products = App.state.current.products;
  for (let id of products) {
    if (!data.product_reco_counts[id]) data.product_reco_counts[id] = 1;
    else data.product_reco_counts[id] = data.product_reco_counts[id] + 1;
  }


  // add filter options to blueprint
  // get current path
  let path = App.state.attr.app.path;
  // if no path defined, do nothing
  if (!path) return;
  // add filter option data to final node
  let endNode = data.blueprint.nodes[App.settings.end];
  endNode.filter_options = endNode.filter_options || {};
  endNode.filter_options[path] = endNode.filter_options[path] || [];
  let filter_options = Util.cloneDeep(App.settings.matrix.last_filter_request);
  Util.arrayPushUnique(endNode.filter_options[path], filter_options);
};

/**
 * Restructure raw walk data into a format usable for CSV generation.
 *
 * [Column headers are created]{@link module:core/matrix~formatChoiceHeaders} by gathering all possible choice groups from results, and [product headers are created]{@link module:core/matrix~formatChoiceHeaders} from the attributes data. Column headers are added to the first row of the final matrix array.
 *
 * Each walk result is then [formatted into a row]{@link module:core/matrix~formatResultRow} matching the headers format, and added to rows.
 *
 * @since 3.0.0
 *
 * @return {array}
 */
const formatResults = () => {
  // get column headers
  data.choiceColumns = formatChoiceHeaders();
  data.productColumns = formatProductHeaders();
  // create an array for each result, starting with column headers
  let rows = [[...data.choiceColumns, ...data.productColumns]];
  // loop through array of results data and format each
  for (let result of data.results) rows.push(formatResultRow(result));
  // return formatted rows
  return rows;
};

/**
 * Finalize blueprint data for use in blueprint view.
 *
 * @returns {object}
 *
 * @see [`formatBlueprintSideJourneys`]{@link module:core/matrix~formatBlueprintSideJourneys}
 * @see [`formatBlueprintRecommendations`]{@link module:core/matrix~formatBlueprintRecommendations}
 * @see [`walkBlueprintTree`]{@link module:core/matrix~walkBlueprintTree}
 */
const formatBlueprint = () => {
  // define start
  let start = App.settings.start;
  // add attribute definitions to blueprint
  data.blueprint.attr_defs = App.attributes;
  // add settings
  data.blueprint.settings = App.settings.matrix.blueprint;
  // start global side journeys array
  data.blueprint.side_journeys = Object.keys(App.settings.destinations);
  // create blueprint flow object
  data.blueprint.flow = {};
  // process flow data
  data.blueprint.flow[start] = walkBlueprintTree(start);
  // setup additional data
  formatBlueprintSideJourneys();
  formatBlueprintRecommendations();
  // return formatted data
  return data.blueprint;
};


/**
 * Recursively walk through all path node destinations, constructing a tree of all possible paths through the app.
 *
 * Beginning with the starting view node, each node's destinations will be looped through. For each destination in a node, a "tree object" is created outside the flow, which will contain any child nodes based on its own destinations. This allows the same node to be used multiple times (i.e. for multiple paths to converge back into a single node) and for all references to that node to contain all relevant child nodes.
 *
 * @param {string} page_id - View ID of the current page being walked.
 * @param {object} [treeObjects] - Object to hold reference objects for path nodes.
 *
 * @returns {*}
 */
const walkBlueprintTree = (page_id, treeObjects = {}) => {
  let node = data.blueprint.nodes[page_id];
  // create tree object, if one does not exist
  if (!treeObjects[page_id]) treeObjects[page_id] = {};
  // if only one destination, set top-level destination value
  if (node.destinations.length === 1) node.destination = node.destinations[0];
  // recurse through path tree
  for (let destination of node.destinations) {
    // skip if the destination object has already been added
    if (treeObjects[page_id][destination]) continue;
    // add destination page object
    treeObjects[page_id][destination] = walkBlueprintTree(destination, treeObjects);
  }
  // add any node side journeys
  Util.arrayPushUnique(data.blueprint.side_journeys, ...node.side_journeys);
  // otherwise return the object
  return treeObjects[page_id];
};

/**
 * Add side journey nodes to blueprint nodes.
 *
 * Side journey destinations are collected from various locations, including the app-wide `destinations` object, node-specific `destinations`, and any `learn_more` destinations found throughout the app path.
 *
 * For each view ID added to `data.blueprint.side_journeys`, a path node is created under the ID in `data.blueprint.nodes` to represent that view in the blueprint. It can then be "pathed" to in the blueprint.
 */
const formatBlueprintSideJourneys = () => {
  // process side journey data
  for (let i = 0; i < data.blueprint.side_journeys.length; i++) {
    let page_id = data.blueprint.side_journeys[i];
    if (data.blueprint.nodes[page_id]) continue;
    // get side journey content and transform it
    let view = App.getContent(page_id);
    App.transformContent(view, '', [], {id: false, blob: false, object: false});
    // gather any new side journeys
    let side_journeys = Object.keys(view['settings'] && view['settings'].destinations || {});
    Util.arrayPushUnique(data.blueprint.side_journeys, ...side_journeys)
    // add page data to blueprint
    data.blueprint.nodes[page_id] = {
      title: view['title'],
      side_journeys,
    }
  }
};

/**
 * Collect and process data to display in the final recommendation node.
 *
 * - Product recommendation frequencies processed and sorted.
 * - Filter requests for each path compared for shared settings and cleaned of duplicates.
 */
const formatBlueprintRecommendations = () => {
  // setup recommendation data
  let endNode = data.blueprint.nodes[App.settings.end];
  endNode.product_reco_counts = [];
  let totalRecommendations = data.results.length;
  for (let [id, count] of Object.entries(data.product_reco_counts)) {
    let product = Filter.getProductsByID(id);
    let name = product[0].name;
    let pct = Util.round((count / totalRecommendations) * 100, 1);
    endNode.product_reco_counts.push({id, name, count, pct});
  }
  endNode.product_reco_counts.sort((a, b) => b.count - a.count);

  // compare recommend filter requests
  let sharedRequest = false;
  let filterOptions = data.blueprint.nodes[App.settings.end].filter_options;
  if (endNode) {
    // loop through requests to find shared values
    for (let requests of Object.values(filterOptions)) {
      for (let request of requests) {
        // if sharedRequest has not been set, copy first request object
        if (!sharedRequest) {
          sharedRequest = Object.assign({}, request);
          continue;
        }
        // check each value against shared properties
        for (let [key, value] of Object.entries(request)) {
          if (!sharedRequest.hasOwnProperty(key)) continue;
          else if (sharedRequest[key] !== value) delete sharedRequest[key];
        }
      }
    }
    // loop through requests again to remove any shared values from request objects
    for (let [path, requests] of Object.entries(filterOptions)) {
      for (let i = 0; i < requests.length; i++) {
        let request = requests[i];
        for (let key of Object.keys(sharedRequest)) {
          if (request.hasOwnProperty(key)) delete request[key];
        }
        // if no keys remain in request, remove it from requests
        if (!Object.keys(request).length) requests.splice(i, 1);
      }
      // if no requests remain in path, remove it
      if (!filterOptions[path].length) delete filterOptions[path];
    }
  }
  filterOptions.all = [sharedRequest];
};

/**
 * Choice headers represent the "group labels" (often a single view, e.g. "I will use my PC:") that each choice may fall under.
 *
 * It is most logical for group labels to be be listed in the order they were walked, but since different walk paths may come across different groups, a system that can get *all choices across the entire matrix in order* is needed. Otherwise, the columns from the first walk would be in order, and later walks with a new group would be added after all the columns in the first. If the later walk shares any of the columns with the first, they will be out of order compared to the the order they were walked.
 *
 * An example of what we **don't** want:
 *
 * ```
 * // example paths
 * Walk 1: "Categories" --> "Productivity" --> "Brand"
 * Walk 2: "Categories" --> "Entertainment" --> "Creativity" --> "Brand"
 *
 * // order of columns if each walk path was looked one at a time
 * // walk #2 choices are out of order
 *          | Categories   | Productivity  | Brand         | Entertainment | Creativity
 * Walk 1   | Choice 1     | Choice 2      | Choice 3      |               |
 * Walk 2   | Choice 1     |               | Choice 4      | Choice 2      | Choice 3
 * ```
 *
 * To solve this problem, walk results are first sorted by length, then choice group names from all results are gathered one index at a time; i.e. all of the first choices are checked for unique group names, then all of the second choices, then third, etc. This ensures that any deviations in path will result in columns that are in order for any given walk (for typical usages):
 *
 * ```
 * // example paths, sorted by length
 * Walk 2: "Categories" --> "Entertainment" --> "Creativity" --> "Brand"
 * Walk 1: "Categories" --> "Productivity" --> "Brand"
 *
 * // column order when considering all results in sequential steps
 * // all walk choices are in the correct order
 *          | Categories   | Entertainment | Productivity  | Creativity    | Brand
 * Walk 1   | Choice 1     |               | Choice 2      |               | Choice 3
 * Walk 2   | Choice 1     | Choice 2      |               | Choice 3      | Choice 4
 * ```
 *
 * The original walk results are still used to build result rows, in order to keep the original walk order.
 *
 * *Note that it is still possible to create out-of-order choices with a sufficiently convoluted path. This would be an issue if group labels shared by multiple paths are found in different orders in each path; e.g., group A comes before group B in one path, but vice versa in another. However, this is unlikely to occur in typical SRT usages, which typically expand or contract parts of each path (e.g. usage categories) rather than changing group orders drastically.*
 *
 * @since 3.0.0
 *
 * @return {array}
 *
 * @example
 * // choice column headers array
 * ["Find the right PC for you", "Everyday tasks", "Productivity", "Entertainment", "Creativity"]
 */
const formatChoiceHeaders = () => {
  let choiceHeaders = [];
  let hasChoices = true;
  let pathIndex = 0;

  // sort result choices by length
  let sortedResults = data.results.slice().sort((a, b) => b.choices.length - a.choices.length);

  // gather all choice columns
  while (hasChoices) {
    // set choices to zero initially, so that we will only continue as long as choices are found
    hasChoices = 0;
    // loop through all results
    for (let result of sortedResults) {
      let choice = result.choices[pathIndex];
      // if we have no choice at this index, skip it
      if (!choice) continue;
      // increment found choices
      hasChoices++;
      // add label, if not already added
      Util.arrayPushUnique(choiceHeaders, choice.group);
    }
    // increment path index
    pathIndex++;
  }

  return choiceHeaders;
};

/**
 * Create column headers for product recommendation results.
 *
 * Product column headers consist of a group of columns: the product name, along with a column for each attribute in the `data.attributes` array (see `attributes` setting in [matrix]{@link module:core/config.matrix} configuration for information on using product attributes). Each column group is repeated for each recommended product result. The total number of column group repetitions is based on the largest product recommendation found in the results.
 *
 * @since 3.0.0
 *
 * @return {array}
 *
 * @example
 * // attributes
 * data.attributes: ['processor', 'form_factor', 'storage_amount']
 *
 * // product column headers
 * // assuming there are max three product recommendations found in results
 * [
 *   'Product 1',
 *   'processor',
 *   'form_factor',
 *   'storage_amount',
 *   'Product 2',
 *   'processor',
 *   'form_factor',
 *   'storage_amount',
 *   'Product 3',
 *   'processor',
 *   'form_factor',
 *   'storage_amount',
 * ]
 */
const formatProductHeaders = () => {
  // find max number of products recommended
  let productCounts = data.results.map(result => result.products.length);
  data.product_count = Math.max(...productCounts);

  // create product column headers
  let productColumns = [];
  for (let i = 0; i < data.product_count; i++) {
    // get product at the current index
    let id = `Product ${i + 1}`;
    // add product name label
    productColumns.push(id);
    // add product attribute labels
    for (let attr of data.attributes) {
      productColumns.push(attr);
    }
  }

  return productColumns;
};

/**
 * Create an array of values for each walk's path and result, which align with column headers.
 *
 * Result row formatting is handled in two parts: choices, which represent the selections made during a walk; and products, which represent the products recommended at the end of a walk.
 *
 * #### Choices
 *
 * Choice column headers are looped and compared to each choice's group label. If they are a match, choice label is added to the row array; otherwise an empty string is added so the column remains blank.
 *
 * #### Products
 *
 * Since product recommendation results can vary in length, a loop the size of the max possible recommendation (set in [`formatProductHeaders`]{@link module:core/matrix~formatProductHeaders}) is run, and the current result is checked for a product at each index. If one exists, its name and attributes are added to the row array.
 *
 * @since 3.0.0
 *
 * @param result
 *
 * @return {array}
 */
const formatResultRow = (result) => {
  // hold values for this row
  let row = [];
  // check choice columns
  for (let column of data.choiceColumns) {
    let value = '';
    // look at each choice to see if this column has a matching value
    for (let choice of result.choices) {
      // if this choice matches column, add index of value then exit
      if (choice.group === column) {
        value = choice.label;
        //value = matrix.legend.indexOf(choice.label);
        break;
      }
    }
    row.push(value);
  }

  // add product values
  for (let i = 0; i < data.product_count; i++) {
    // get product at the current index
    let product = result.products[i];
    // add product name
    row.push(product ? product.name : '');
    // add product attribute values
    for (let attr of data.attributes) {
      let productAttr = product ? product.attributes[attr] : '';
      if (Array.isArray(productAttr)) productAttr = productAttr.join(', ');
      else if (typeof productAttr === 'object') productAttr = 'object';
      row.push(productAttr || '');
    }
  }

  return row;
};
